import { useLocation, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { compact } from 'lodash'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { CUSTOM_FILTERS_SEARCH_PARAM } from 'app/components/analytics/DashboardHeader'
import { CallsModal } from 'app/components/scorecards/CallsModal'
import { decodeFilters, FilterOperator, PredefinedFilter } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import useLocationMetrics from 'app/hooks/useLocationMetrics'
import useStaffMetrics from 'app/hooks/useStaffMetrics'
import { closeModal, showModal } from 'app/lib/modal'
import {
  defaultLocationColumns,
  getDefaultStaffColumns,
  callTypeTitle,
  getScorecardStaffColumns,
} from 'app/table-columns/analytics/marketing'
import { hasRole, useUser } from 'app/utils'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { gqlQuery } from 'app/lib/graphqlClient'
import { debug } from 'lib/logging'
import type { StaffBreakdownRow as StaffRow } from 'lib/staff-breakdown'
import { defaultNonPatientFilter, defaultBecameLeadDateFilter } from 'app/components/analytics-marketing/defaultFilters'
import { graphql } from 'app/generated/gql'

const MARKETING_LOCATIONS_QUERY = graphql(`
  query MarketingLocations {
    locations {
      hits {
        id
        name
        tags
        orgTags
        pmsIntegrated
        lastPmsSyncDate
        workspaceId
      }
    }
  }
`)

const STAFF_BREAKDOWN_QUERY = graphql(`
  query MarketingStaffBreakdown(
    $dateRange: DateRangeInput!
    $channel: String
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
  ) {
    staffBreakdown(
      dateRange: $dateRange
      channel: $channel
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
    ) {
      hits {
        bucket
        id
        date
        isStaff
        newPatientCalls
        qualifiedNPCalls
        disqualifiedNPCalls
        scheduledNPCalls
        unscheduledNPCalls
        averageDuration
        speedToLead
        NPCallScheduleRate
      }
    }
  }
`)

export type UseMarketingReturn = {
  user: ReturnType<typeof useUser>
  channel: string
  filters: string | undefined
  fromDate: string
  toDate: string
  compareFromDate: string
  compareToDate: string
  isComparingToGoals: boolean
  dontCompare: boolean
  platform: string | null
  showDisqualified: boolean
  openTab: string
  canViewStaffBreakdown: boolean
  canViewMarketingSpend: boolean
  staffDashboardV2: boolean
  summary: any
  compareTo: any
  locationRows: any
  isLocationsLoading: boolean
  staffRows: any
  isStaffLoading: boolean
  scorecardStaffRows: StaffRow[] | undefined
  isScorecardStaffLoading: boolean
  locationColumns: any
  staffColumns: any
  locationIds: string[]
  integrationStatus: string
  defaultFilters: PredefinedFilter
  dateChangeHandler: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  handleTabChange: (tab: string) => void
  handleShowDisqualifiedToggle: (value: boolean) => void
  setFilters: (filters: string | undefined) => void
  setLocationSearchQuery: (query: string) => void
  setStaffSearchQuery: (query: string) => void
}

export function useMarketing(): UseMarketingReturn {
  const user = useUser()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const staffDashboardV2 = useFeatureToggle(`staff-dashboard-v2`)
  const checkPermission = useCheckPermission()
  const isOrgUser = useIsOrgUser()

  const canViewMarketingSpend = staffV2Enabled ? checkPermission([`org:marketing:see-spend`, `marketing:spend`]) : true
  const canViewOwnPerformance = staffV2Enabled ? checkPermission(`staff:performance:own`) : true
  const canViewOthersPerformance = staffV2Enabled ? checkPermission(`staff:performance:others`) : true
  const canViewStaffBreakdown = isOrgUser || canViewOwnPerformance || canViewOthersPerformance

  const [locationSearchQuery, setLocationSearchQuery] = useState<string>(``)
  const [staffSearchQuery, setStaffSearchQuery] = useState<string>(``)
  const [channel, setChannel] = useState<string>(searchParams.get(`channel`) ?? ``)
  const channelOp = searchParams.get(`channelOp`)
  const [filters, setFilters] = useState<string | undefined>(searchParams.get(CUSTOM_FILTERS_SEARCH_PARAM) || ``)
  const [openTab, setOpenTab] = useState<string>(searchParams.get(`tab`) ?? `Summary`)
  const locationIdsMemoed = useMemo(() => searchParams.get(`locationIds`), [searchParams])
  const locationIds = useMemo(() => compact(locationIdsMemoed?.split(`,`) ?? []), [locationIdsMemoed])
  const integrationStatus = useMemo(() => searchParams.get(`integrationStatus`) ?? ``, [searchParams])
  const [fromDate, setFromDate] = useState<string>(searchParams.get(`fromDate`) ?? ``)
  const [toDate, setToDate] = useState<string>(searchParams.get(`toDate`) ?? ``)
  const [compareFromDate, setCompareFromDate] = useState<string>(searchParams.get(`compareFromDate`) ?? ``)
  const [compareToDate, setCompareToDate] = useState<string>(searchParams.get(`compareToDate`) ?? ``)
  const [isComparingToGoals, setIsComparingToGoals] = useState(false)
  const [platform, setPlatform] = useState<string | null>(searchParams.get(`platform`))
  const [showDisqualified, setShowDisqualified] = useState(searchParams.get(`showDisqualified`) === `true`)
  const [, summary, rawLocationRows, compareTo, isLocationsLoading] = useLocationMetrics(
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    locationIds,
    channel,
    channelOp,
    locationSearchQuery,
    integrationStatus,
    filters,
    platform,
    showDisqualified,
  )

  const [locationsMap, setLocationsMap] = useState<Record<string, any>>({})
  useEffect(() => {
    gqlQuery(MARKETING_LOCATIONS_QUERY, {}).then(({ data }) => {
      const map: Record<string, any> = {}
      for (const loc of data?.locations?.hits ?? []) map[loc.id] = loc
      setLocationsMap(map)
    })
  }, [])

  const locationRows = useMemo(() => {
    if (!rawLocationRows) return undefined
    return rawLocationRows.map((row) => {
      const loc = locationsMap[row.locationId]
      return loc ? { ...row, location: loc } : row
    })
  }, [rawLocationRows, locationsMap])
  const [staffRows, isStaffLoading] = useStaffMetrics(
    fromDate,
    toDate,
    locationIds,
    channel,
    staffSearchQuery,
    integrationStatus,
    filters,
    platform,
    !staffDashboardV2,
  )

  const [scorecardStaffRowsRaw, setScorecardStaffRowsRaw] = useState<StaffRow[]>()
  const [isScorecardStaffLoading, setIsScorecardStaffLoading] = useState(false)

  const scorecardStaffRows = useMemo(() => {
    if (!scorecardStaffRowsRaw) return undefined
    if (!staffSearchQuery) return scorecardStaffRowsRaw
    const q = staffSearchQuery.toLowerCase()
    return scorecardStaffRowsRaw.filter((row) => row.bucket.toLowerCase().includes(q))
  }, [scorecardStaffRowsRaw, staffSearchQuery])

  useEffect(() => {
    if (!staffDashboardV2 || !fromDate || !toDate) return
    setIsScorecardStaffLoading(true)
    bg(() => {
      return gqlQuery(STAFF_BREAKDOWN_QUERY, {
        dateRange: { fromDate, toDate },
        locationIds: locationIds.length ? locationIds : undefined,
        channel: channel || undefined,
        integrationStatus: integrationStatus || undefined,
        filters: filters || undefined,
      }).then((result) => {
        setScorecardStaffRowsRaw(result.data?.staffBreakdown?.hits ?? [])
        setIsScorecardStaffLoading(false)
      })
    })
  }, [staffDashboardV2, fromDate, toDate, locationIds, channel, integrationStatus, filters])

  // Deep link: open CallsModal when ?staff=X&callType=Y are in the URL
  useEffect(() => {
    const staffParam = searchParams.get(`staff`)
    const staffIdParam = searchParams.get(`staffId`)
    const callType = searchParams.get(`callType`)
    if (!staffDashboardV2 || !staffParam || !callType || !fromDate || !toDate) return
    // Clear params immediately so the effect doesn't re-fire on reload
    setSearchParams((prev) => {
      prev.delete(`staff`)
      prev.delete(`staffId`)
      prev.delete(`callType`)
      return prev
    })
    const isMatched = staffIdParam && staffIdParam !== staffParam
    showModal(
      <CallsModal
        title={callTypeTitle(callType)}
        subtitle={staffParam}
        {...(isMatched ? { staffId: staffIdParam } : { staffName: staffParam })}
        type={callType as any}
        fromDate={fromDate}
        toDate={toDate}
        channel={channel}
        locationIds={locationIds}
        filters={filters}
        onClose={() => closeModal()}
      />,
      {
        hideTitle: true,
        removePadding: true,
        isFullScreen: true,
        useRadix: true,
      },
    )
  }, [staffDashboardV2, fromDate, toDate])

  const legacyShowOrgAdminColumns = hasRole(user!, `orgAdmin`)
  const permissionShowOrgAdminColumns = checkPermission([`org:marketing:see-spend`, `marketing:spend`])
  const showOrgAdminColumns = staffV2Enabled ? permissionShowOrgAdminColumns : legacyShowOrgAdminColumns

  const locationColumns = useMemo(
    () =>
      defaultLocationColumns(
        showOrgAdminColumns,
        isSMCOrg(user?.orgId),
        location.search,
        canViewMarketingSpend,
        staffV2Enabled,
      ),
    [user, showOrgAdminColumns, canViewMarketingSpend, staffV2Enabled],
  )

  const defaultFilters: PredefinedFilter = {
    ...(searchParams.has(`platform`) && searchParams.get(`platform`) !== ``
      ? {
          [`platform`]: {
            operator: `exactly` as FilterOperator,
            value: searchParams.get(`platform`),
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`dashboardFilters`) && searchParams.get(`dashboardFilters`) !== ``
      ? decodeFilters(searchParams.get(`dashboardFilters`)!).reduce(
          (acc, filter) => ({
            ...acc,
            [filter.attribute]: {
              operator: filter.operator as FilterOperator,
              value: filter.value,
              readOnly: true,
            },
          }),
          {},
        )
      : undefined),
    ...(searchParams.has(`filters`) ? undefined : defaultNonPatientFilter),
    ...(searchParams.has(`channel`)
      ? {
          [`channel.keyword`]: {
            operator: `in` as FilterOperator,
            value: [searchParams.get(`channel`)!],
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`locationIds`)
      ? {
          [`locationId`]: {
            operator: `in` as FilterOperator,
            value: compact(decodeURIComponent(searchParams.get(`locationIds`)!).split(`,`)),
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`fromDate`) && searchParams.has(`toDate`)
      ? {
          [`becameLeadDate`]: {
            operator: `between` as FilterOperator,
            value: [searchParams.get(`fromDate`)!, searchParams.get(`toDate`)!],
            readOnly: true,
          },
        }
      : defaultBecameLeadDateFilter),
  }
  debug(`marketing`, `default filters`, defaultFilters)

  const staffColumns = useMemo(
    () =>
      staffDashboardV2
        ? getScorecardStaffColumns(
            fromDate,
            toDate,
            channel,
            defaultFilters,
            (staff, staffId, callType) => {
              const isMatched = staffId && staffId !== staff
              showModal(
                <CallsModal
                  title={callTypeTitle(callType)}
                  subtitle={staff}
                  {...(isMatched ? { staffId } : { staffName: staff })}
                  type={callType as any}
                  fromDate={fromDate}
                  toDate={toDate}
                  channel={channel}
                  locationIds={locationIds}
                  filters={filters}
                  onClose={() => closeModal()}
                />,
                {
                  hideTitle: true,
                  removePadding: true,
                  isFullScreen: true,
                  useRadix: true,
                },
              )
            },
            { channel, locationIds, filters },
          )
        : getDefaultStaffColumns(fromDate, toDate, channel, defaultFilters),
    [fromDate, toDate, channel, staffDashboardV2, locationIds, filters],
  )

  const [dontCompare, setDontCompare] = useState(false)

  useEffect(() => {
    setChannel(searchParams.get(`channel`) ?? ``)
    setPlatform(searchParams.get(`platform`) ?? ``)
  }, [searchParams])

  const dateChangeHandler = (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => {
    setFromDate(fromDate)
    setToDate(toDate)
    setCompareFromDate(compareFromDate)
    setCompareToDate(compareToDate)
    setIsComparingToGoals(comparingGoals)
    setDontCompare(dontCompare)
  }

  const handleTabChange = (tab: string) => {
    setOpenTab(tab)
    setSearchParams((prev) => {
      prev.set(`tab`, tab)
      return prev
    })
  }

  const handleShowDisqualifiedToggle = (value: boolean) => {
    setShowDisqualified(value)
    setSearchParams((prev) => {
      prev.set(`showDisqualified`, `${value}`)
      return prev
    })
  }

  return {
    user,
    channel,
    filters,
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    isComparingToGoals,
    dontCompare,
    platform,
    showDisqualified,
    openTab,
    canViewStaffBreakdown,
    canViewMarketingSpend,
    staffDashboardV2,
    summary,
    compareTo,
    locationRows,
    isLocationsLoading,
    staffRows,
    isStaffLoading,
    scorecardStaffRows,
    isScorecardStaffLoading,
    locationColumns,
    staffColumns,
    locationIds,
    integrationStatus,
    defaultFilters,
    dateChangeHandler,
    handleTabChange,
    handleShowDisqualifiedToggle,
    setFilters,
    setLocationSearchQuery,
    setStaffSearchQuery,
  }
}
